import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { createFlagResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

export const postNewFlag = async (messageData: {
	customerId: string;
	message: string;
	poNumber: string;
	date: Date;
}) => {
	const url = apiRoutes.POSTCREATEFLAG;
	const resp = await postAndValidateTraxAr(
		url,
		{
			customerId: Number(messageData.customerId),
			shipmentId: Number(messageData.poNumber),
			message: messageData.message,
			effectiveDate: messageData.date,
		},
		createFlagResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostNewFlag = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (messageData: {
			customerId: string;
			message: string;
			poNumber: string;
			date: Date;
		}) => postNewFlag(messageData),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Flag created.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to create flag. Please try again.",
			);
		},
	});

	return {
		postNewFlag: mutate,
		postNewFlagAsync: mutateAsync,
		isPostingNewFlag: isPending,
		error,
		data,
		resetError: reset,
	};
};
